import {Controller} from '@hotwired/stimulus';
import {attachmentError, attachmentLimits, fileExtension} from '../js/attachments';

/**
 * Staged file attachments (chips + remove) for the booking pop-ups. State lives on the instance
 * so accept and modify can each stage independently. The modal reads getFiles() and resets clear().
 */
/* stimulusFetch: 'lazy' */
export default class extends Controller {
    static targets = [
        'input',
        'list',
    ];

    static values = {
        // Per-instance byte cap. The booking modals set a lower one than the chat composer
        // because they upload multipart (bounded by PHP post_max_size), not via S3 presign.
        maxBytes: Number,
    };

    staged = [];

    disconnect() {
        this.clear();
    }

    // Public API for the owning modal controller.
    getFiles() {
        return this.staged.map(item => item.file);
    }

    clear() {
        this.staged.forEach(item => {
            if (item.previewUrl) {
                URL.revokeObjectURL(item.previewUrl);
            }
        });

        this.staged = [];
        this.render();
    }

    openPicker() {
        this.inputTarget.click();
    }

    onPick() {
        this.add(this.inputTarget.files);
        // Reset so re-picking the same file still fires change.
        this.inputTarget.value = '';
    }

    add(fileList) {
        if (!fileList || fileList.length === 0) {
            return;
        }

        const maxFiles = attachmentLimits().maxFiles;
        let capReached = false;

        for (const file of fileList) {
            if (this.staged.length >= maxFiles) {
                capReached = true;

                break;
            }

            if (!this.isValid(file)) {
                continue;
            }

            this.staged.push({
                file: file,
                previewUrl: file.type.startsWith('image') ? URL.createObjectURL(file) : null,
            });
        }

        if (capReached) {
            const message = translations['messages.Attachment count limit'] || 'You can attach up to %count% files at a time.';
            addAlert('danger', message.replace('%count%', maxFiles), '', 5000);
        }

        this.render();
    }

    remove(event) {
        const index = Number(event.currentTarget.dataset.index);
        const [removed] = this.staged.splice(index, 1);

        if (removed && removed.previewUrl) {
            URL.revokeObjectURL(removed.previewUrl);
        }

        this.render();
    }

    render() {
        this.listTarget.innerHTML = '';

        if (this.staged.length === 0) {
            this.listTarget.classList.add('d-none');

            return;
        }

        const removeLabel = translations['messages.Remove file'] || 'Remove file';

        this.staged.forEach((item, index) => {
            const extension = fileExtension(item.file.name);

            const preview = item.previewUrl
                ? `<img src="${item.previewUrl}" class="staged-attachment-thumb" alt="" draggable="false">`
                : `<span class="staged-attachment-ext">.${this.escape(extension)}</span>`;

            const chip = document.createElement('div');
            chip.className = 'staged-attachment';
            chip.title = item.file.name;
            chip.innerHTML = `
                <div class="staged-attachment-preview">${preview}</div>
                <button
                    type="button"
                    class="staged-attachment-remove"
                    data-tooltip="${this.escape(removeLabel)}"
                    aria-label="${this.escape(removeLabel)}"
                    data-index="${index}"
                    data-action="attachment-staging#remove"
                >
                    <i class="uil uil-multiply"></i>
                </button>`;

            this.listTarget.appendChild(chip);
        });

        this.listTarget.classList.remove('d-none');
    }

    isValid(file) {
        const limits = attachmentLimits();
        const error = attachmentError(file, {
            // The booking multipart path caps lower than the chat's 100M (post_max_size).
            maxBytes: this.maxBytesValue || limits.maxBytes,
            allowedExtensions: limits.allowedExtensions,
        });

        if (error) {
            addAlert('danger', translations[error] || '', '', 5000);

            return false;
        }

        return true;
    }

    escape(value) {
        const element = document.createElement('div');
        element.textContent = value;

        return element.innerHTML;
    }
}
